import {
    MAX_CLUBS_PER_BAG, MODE_SERIES_SIZE, MODE_SUGGESTED_SHOTS,
} from '@golf/common';
import type { ShotOutcome, TrainingModeCode } from '@golf/common';
import { ArrowLeft, Award, BarChart3, Briefcase, Check, ChevronRight, Flag, LogOut, Pencil, Play, Plus, Repeat, RotateCcw, Target, Trash2, Undo2, Users, Wrench, X } from 'lucide-react';

/** Le texte posé À MÊME le fond vert.
 *
 *  Les écrans d'entraînement portent `text-base-content` sur leur conteneur,
 *  pour que les cartes opaques posées dessus héritent d'une encre sombre. Tout
 *  ce qui n'est PAS dans une carte hérite alors de cette même encre sombre —
 *  sur le vert profond, mesuré à 1,41:1. C'est invisible, et ça l'était pour
 *  le libellé « Date et heure », le rappel d'étape et le mémo du mode libre.
 *
 *  Le titre et le lien de retour appliquaient déjà ce traitement à la main :
 *  il est nommé une fois, pour que le prochain texte ajouté sur cet écran
 *  n'ait pas à le redécouvrir. L'ombre porte le bord net si une carte claire
 *  remonte dessous au défilement. */
const SUR_LE_VERT = 'text-white drop-shadow-[0_1px_3px_rgba(0,0,0,0.55)]';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useHaptics } from '../../hooks/useHaptics.ts';
import { ApiError } from '../../lib/api.ts';
import type { Bag } from '../../lib/equipment.ts';
import { formatDurationShort } from '../../lib/duration.ts';
import { listBags } from '../../lib/equipment.ts';
import { ClubLabel } from '../../components/ClubLabel.tsx';
import {
    ChoixDuClub, MarqueTuile, PourquoiCeClub, RaisonLigne,
} from '../../components/ChoixDuClub.tsx';
import { Etape, Valeur } from '../../components/Etape.tsx';
import { useSettings } from '../../lib/settings.tsx';
import { formatDistance } from '../../lib/units.ts';
import type { Series, Session, Shot, Stats } from '../../lib/training.ts';
import type { TrainingMode } from '../../lib/training.ts';
import {
    changeSeriesClub, completeSeries, deleteShot, discardSeries, finishSession, fromLocalInput,
    garderSeance,
    getCurrent, getStats, listModes, recordShot, startSeries, startSession,
    toLocalInput, undoLastShot, updateShot,
} from '../../lib/training.ts';

import { clubsToFocus, clubsToWork } from '../../lib/clubFocus.ts';
import { MODES } from '../../lib/modes.ts';
import type { CustomExercise, RunState } from '../../lib/planning.ts';
import { etapeFaite, finishRun, getCurrentRun, listExercises } from '../../lib/planning.ts';
import { describe } from './WorkoutComposer.tsx';
import { DistanceStepper } from './DistanceStepper.tsx';
import { PaveCoup } from './PaveCoup.tsx';
import { PavePoints } from './PavePoints.tsx';
import { ModeIcon } from './ModeIcon.tsx';
import { ShotEditor } from './ShotEditor.tsx';

type BagClub = Bag['clubs'][number];

/** Puce des modes jouables à plusieurs, à cheval sur le coin de la carte.
 *
 *  Posée sur le bord plutôt que dans le titre : c'est une PROPRIÉTÉ du mode, pas
 *  une partie de son nom. Dans le flux du texte elle repoussait le libellé et
 *  attirait l'œil autant que lui, alors qu'elle ne se lit qu'une fois — on
 *  apprend en une seconde que le putting se joue à deux, et on n'a plus besoin
 *  qu'on le répète à chaque visite.
 *
 *  L'anneau couleur du fond détache le disque du bord qu'il chevauche : sans
 *  lui, la puce et la carte se confondent en une bosse.
 *
 *  Sans texte, donc avec un nom accessible explicite — un lecteur d'écran n'a
 *  pas de coin de carte à regarder. */
function MultiplayerBadge() {
    return (
        <span
            className="absolute -right-1.5 -top-1.5 flex size-7 items-center justify-center rounded-full bg-primary text-primary-content ring-4 ring-base-100"
            title="Jouable à plusieurs"
        >
            <Users size={14} aria-hidden="true" />
            <span className="sr-only">Jouable à plusieurs</span>
        </span>
    );
}

/** Message d'echec au demarrage.
 *
 *  Le 409 est le garde-fou « un seul exercice ouvert a la fois ». Il est
 *  legitime, mais l'annoncer sans dire ou se trouve cet exercice laissait le
 *  joueur devant un mur : on le conduit donc la-bas, ou il pourra le finir ou
 *  l'abandonner. */
function messageDemarrage(e: unknown): string {
    if (e instanceof ApiError && e.status === 409) {
        return "Un exercice est deja en cours. Ouvrez-le depuis Entrainement pour le finir "
            + "ou l'abandonner, puis revenez ici.";
    }
    return e instanceof ApiError ? e.message : 'Demarrage impossible';
}

export function Training() {
    const navigate = useNavigate();
    const { vibrate } = useHaptics();
    const { unit } = useSettings();

    const [loading, setLoading] = useState(true);
    const [session, setSession] = useState<Session | null>(null);
    const [series, setSeries] = useState<Series[]>([]);
    const [bags, setBags] = useState<Bag[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    /** Série en cours de saisie. Null entre deux séries. */
    const [active, setActive] = useState<Series | null>(null);
    /** Coup réussi en attente de sa distance. */
    const [pendingDistance, setPendingDistance] = useState(false);
    const [confirmStop, setConfirmStop] = useState(false);
    /** Balle en cours de correction, et sélecteur de club de rattrapage. */
    const [editingShot, setEditingShot] = useState<Shot | null>(null);
    const [fixingClub, setFixingClub] = useState(false);
    /** Le sac déplié sous la carte du club, comme sur le parcours. */
    const [sacOuvert, setSacOuvert] = useState(false);
    /* Un verrou, le temps du changement de club.
     *
     * Le sac se déplie d'office tant qu'aucune série n'est ouverte : c'est le
     * premier geste de la séance, il n'a pas à se chercher derrière un
     * chevron. Mais changer de club passe justement par un instant SANS série
     * — on clôt l'ancienne, on ouvre la nouvelle, et deux allers-retours
     * serveur séparent les deux.
     *
     * Pendant cet instant `active` vaut `null`, la règle du dépliage d'office
     * s'appliquait, et le sac se repliait puis se rouvrait puis se refermait :
     * un clignotement à chaque choix de club. Absent du parcours, où choisir
     * un club ne touche à rien d'autre qu'un état local.
     *
     * Le verrou couvre l'opération entière plutôt que de s'appuyer sur
     * `busy` : celui-ci retombe entre les deux appels, et il suffit d'un
     * rendu dans cet interstice pour que le sac reparaisse. */
    const [changementDeClub, setChangementDeClub] = useState(false);
    const [startedAtDraft, setStartedAtDraft] = useState('');
    /** Description de l'exercice, en mode libre. */
    const [notesDraft, setNotesDraft] = useState('');
    const [modes, setModes] = useState<TrainingMode[]>([]);
    /** Horloge de la séance en cours. Rafraîchie à la minute : une séance de
     *  practice se compte en minutes, et un compteur à la seconde donnerait une
     *  précision que la mesure n'a pas — on arrête souvent en rangeant ses
     *  clubs. */
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 30_000);
        return () => clearInterval(id);
    }, []);
    /** Null tant que le joueur n'a pas choisi ce qu'il vient faire. */
    const [chosenMode, setChosenMode] = useState<TrainingModeCode | null>(null);
    /** Exercice personnalisé retenu, s'il y en a un. Il tourne sur le moteur du
     *  mode libre — ses bornes ne changent pas la saisie, elles la cadrent. */
    const [chosenExercise, setChosenExercise] = useState<CustomExercise | null>(null);
    const [exercises, setExercises] = useState<CustomExercise[]>([]);
    /** Statistiques récentes, pour suggérer un club au moment de le choisir.
     *  Chargées à part et sans bloquer : un échec ici coûte un conseil, pas la
     *  saisie — et la saisie est la seule chose dont le joueur a besoin. */
    const [stats, setStats] = useState<Stats | null>(null);
    useEffect(() => { void getStats().then(setStats).catch(() => setStats(null)); }, []);
    const [run, setRun] = useState<RunState['run']>(null);
    const [runItems, setRunItems] = useState<RunState['items']>([]);
    /** Étape de séance qu'on est en train de lancer, s'il y en a une. Retenue
     *  séparément du mode : deux étapes peuvent porter le même exercice, et
     *  c'est le rang qui les distingue. */
    const [runStep, setRunStep] = useState<RunState['items'][number] | null>(null);

    /** Sac retenu pour la séance à venir. Null : on suit le sac actif. */
    const [chosenBagId, setChosenBagId] = useState<string | null>(null);
    const [pickingBag, setPickingBag] = useState(false);

    // Une fois la séance ouverte, c'est SON sac qui compte, quel qu'ait été le
    // choix d'écran — sans quoi une séance reprise afficherait les clubs d'un
    // autre sac que celui avec lequel elle a été jouée.
    const bag = session
        ? bags.find((b) => b.id === session.bagId)
        : (chosenBagId ? bags.find((b) => b.id === chosenBagId) : undefined)
            ?? bags.find((b) => b.isActive)
            ?? bags[0];
    const clubs: BagClub[] = bag?.clubs ?? [];
    /** Sacs proposables : ceux qui contiennent au moins un club. */
    const usableBags = bags.filter((b) => b.clubs.length > 0);

    const load = useCallback(async () => {
        try {
            const [cur, bg, md, ex, rn] = await Promise.all([
                getCurrent(), listBags(), listModes(), listExercises(), getCurrentRun(),
            ]);
            setBags(bg);
            setModes(md);
            setExercises(ex);
            /* Un atelier désigné depuis un bilan arrive par l'adresse. Le
             * retenir ICI et non à l'ouverture de l'écran : la liste n'existe
             * pas encore à ce moment-là, et un identifiant sans exercice en
             * face ne sélectionne rien. */
            const voulu = new URLSearchParams(window.location.search).get('exercice');
            if (voulu && !cur.session) {
                const e = ex.find((x) => x.id === voulu);
                if (e) { setChosenExercise(e); setNotesDraft(e.name); }
            }
            setRun(rn.run);
            setRunItems(rn.items);
            setSession(cur.session);
            setSeries(cur.series ?? []);
            // Reprise : une séance interrompue par un écran verrouillé doit
            // repartir sur la série incomplète, pas en ouvrir une nouvelle.
            // Le critère dépend du mode : une série libre reste ouverte tant
            // que le joueur ne l'a pas close, quel que soit son nombre de
            // balles.
            const size = cur.session
                ? MODE_SERIES_SIZE[(cur.session.mode as TrainingModeCode) || 'triple_shot'] ?? null
                : null;
            const unfinished = (cur.series ?? []).find(
                (s) => s.completedAt === null && (size === null || s.shots.length < size),
            );
            setActive(unfinished ?? null);
            if (cur.session) setStartedAtDraft(toLocalInput(cur.session.startedAt));
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Chargement impossible');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { void load(); }, [load]);

    /* La seance est recopiee sur le telephone a chaque changement : c'est elle
     * qu'on rouvrira si le reseau manque, ou si l'onglet est ferme entre deux
     * series. */
    useEffect(() => {
        // Meme precaution que sur le parcours : ne pas ecraser la copie avec le
        // vide du montage, que le chargement est en train de lire.
        if (session) void garderSeance({ session, series });
    }, [session, series]);

    // ── Démarrage ────────────────────────────────────────────────────────────

    const onStart = async (): Promise<void> => {
        setBusy(true); setError(null);
        try {
            const { session: s } = await startSession({
                // Un exercice personnalisé s'exécute en mode libre : c'est le
                // seul qui n'impose ni taille de série ni club, donc le seul sur
                // lequel des bornes choisies par le joueur peuvent se poser.
                mode: chosenExercise ? 'free' : chosenMode ?? 'triple_shot',
                bagId: bag?.id,
                startedAt: startedAtDraft ? fromLocalInput(startedAtDraft) : undefined,
                // Le nom de l'exercice sert de mémo par défaut : sans lui, la
                // ligne d'historique dirait « séance libre » et rien d'autre.
                notes: notesDraft.trim() || chosenExercise?.name || null,
                customExerciseId: chosenExercise?.id ?? null,
                runId: runStep ? run?.id ?? null : null,
                runPosition: runStep?.position ?? null,
            }, {
                /* Ce que le serveur recopiait dans sa réponse, et que l'écran a
                   déjà : sans réseau, il n'y a pas de réponse à attendre, et la
                   consigne de l'atelier doit s'afficher tout de suite. */
                bagName: bag?.name,
                exercice: chosenExercise ? {
                    name: chosenExercise.name,
                    description: chosenExercise.description,
                    successLabel: chosenExercise.successLabel,
                    criteria: chosenExercise.criteria,
                    ballsPerRound: chosenExercise.ballsPerRound,
                    rounds: chosenExercise.rounds,
                    clubTemplateIds: chosenExercise.clubTemplateIds,
                } : null,
            });
            setSession(s);
            setStartedAtDraft(toLocalInput(s.startedAt));
            setSeries([]); setActive(null);
            // La relecture est un CONFORT — elle ramène ce que le serveur sait
            // en plus — et non une condition : sans réseau, l'état qu'on vient
            // de poser est déjà le bon, et échouer ici effacerait la séance
            // sous les doigts du joueur.
            await load().catch(() => undefined);
        } catch (e) {
            setError(messageDemarrage(e));
        } finally { setBusy(false); }
    };

    // ── Saisie ───────────────────────────────────────────────────────────────

    const onPickClub = async (clubId: string): Promise<void> => {
        if (!session) return;
        setBusy(true); setError(null);
        try {
            const { series: s } = await startSeries(session.id, clubId, series.length + 1);
            setActive(s);
            setSeries((prev) => [...prev, s]);
            vibrate('tap');
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Impossible de démarrer la série');
        } finally { setBusy(false); }
    };

    /* Le jeton de la balle en cours d'écriture.
     *
     * Il SURVIT à l'échec, et c'est tout l'intérêt : une requête perdue en route
     * laisse le joueur devant « Coup non enregistré » alors qu'elle a pu être
     * reçue. Il retape, le même jeton part, et le serveur rend la balle déjà
     * écrite au lieu d'en ajouter une seconde. Effacé au succès : la balle
     * suivante est une autre balle.
     *
     * Il se renouvelle aussi quand le résultat change. Retaper « mauvais
     * contact » après avoir tenté « bon contact », c'est se corriger, pas
     * rejouer : la même clé rendrait alors la première version.
     *
     * `randomUUID` n'existe qu'en contexte sécurisé — en HTTP simple sur une IP
     * de réseau local il est absent. Sans jeton, on retombe simplement sur le
     * comportement d'avant. */
    const jetonBalle = useRef<{ pour: ShotOutcome; jeton: string } | null>(null);

    const applyShot = async (outcome: ShotOutcome, distanceM?: number | null): Promise<void> => {
        if (!active) return;
        setBusy(true);
        if (jetonBalle.current?.pour !== outcome) {
            const neuf = globalThis.crypto?.randomUUID?.();
            jetonBalle.current = neuf ? { pour: outcome, jeton: neuf } : null;
        }
        try {
            const { series: s } = await recordShot(
                active, outcome, distanceM, jetonBalle.current?.jeton,
            );
            jetonBalle.current = null;
            setActive(s);
            setSeries((prev) => prev.map((x) => (x.id === s.id ? s : x)));
            vibrate(outcome === 'center' ? 'success' : 'error');
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Coup non enregistré');
        } finally { setBusy(false); }
    };

    const onOutcome = (outcome: ShotOutcome): void => {
        /* En mode EXAMEN, on ne demande pas la distance.
         *
         * Un atelier se juge sur un critère qui n'a rien à voir avec elle : le
         * putt est rentré, la balle est dans le couloir. Interrompre la série
         * pour un sélecteur de mètres à chaque balle réussie ferait trois
         * gestes là où l'examinateur en attend un — et la mesure ne servirait
         * à rien, puisque ces balles sont hors des statistiques.
         *
         * Lu sur la séance et non sur la variable calculée plus bas : celle-ci
         * n'existe qu'après les retours anticipés, et dépendre de cet ordre
         * serait un piège pour la prochaine retouche. */
        if (outcome === 'center' && session?.exerciseSuccessLabel == null) {
            // Le coup n'est pas encore écrit : on demande d'abord la distance,
            // parce qu'un coup réussi sans mesure et un coup réussi mesuré
            // sont deux enregistrements différents.
            vibrate('success');
            setPendingDistance(true);
            return;
        }
        if (outcome === 'center') vibrate('success');
        void applyShot(outcome);
    };

    /** Referme la série en cours et revient au choix du club.
     *
     *  Un seul geste, quel que soit l'état : une série qui porte des balles est
     *  close et gardée, une série encore vide est supprimée — un club ouvert
     *  par erreur ne doit pas laisser de coquille qui compterait dans le nombre
     *  de séries sans porter une balle. */
    /* Prendre un club depuis le sac déplié — le geste unique du parcours,
     * transposé.
     *
     * Sur le parcours, toucher une tuile désigne le club du PROCHAIN coup et
     * rien d'autre : les balles déjà jouées gardent le leur. Ici la même
     * intention se traduit en deux appels — clore la série en cours, en ouvrir
     * une avec le club choisi — parce qu'au practice une série EST liée à un
     * club. Le joueur, lui, fait le même geste et obtient la même chose.
     *
     * Toucher le club déjà en cours ne fait que refermer le sac : c'est
     * « garder », dit par le geste plutôt que par un bouton. */
    const passerAuClub = async (clubId: string): Promise<void> => {
        setSacOuvert(false);
        if (active?.clubId === clubId) return;
        setChangementDeClub(true);
        try {
            if (active) await onChangeClub();
            await onPickClub(clubId);
        } finally { setChangementDeClub(false); }
    };

    const onChangeClub = async (): Promise<void> => {
        if (!active) return;
        setBusy(true);
        setError(null);
        try {
            if (active.shots.length === 0) {
                await discardSeries(active.id);
                setSeries((prev) => prev.filter((x) => x.id !== active.id));
            } else {
                const { series: s } = await completeSeries(active);
                setSeries((prev) => prev.map((x) => (x.id === s.id ? s : x)));
                vibrate('success');
            }
            setActive(null);
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Changement de club impossible');
        } finally { setBusy(false); }
    };

    /** Corrections d'une balle déjà saisie, et du club de la série. */
    const onEditShot = async (
        patch: { outcome?: ShotOutcome; distanceM?: number | null },
    ): Promise<void> => {
        if (!editingShot || !active) return;
        setBusy(true); setError(null);
        try {
            const { series: s } = await updateShot(active, editingShot.id, patch);
            setActive(s);
            setSeries((prev) => prev.map((x) => (x.id === s.id ? s : x)));
            setEditingShot(null);
            vibrate('tap');
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Correction impossible');
        } finally { setBusy(false); }
    };

    const onDeleteShot = async (): Promise<void> => {
        if (!editingShot || !active) return;
        setBusy(true); setError(null);
        try {
            const { series: s } = await deleteShot(active, editingShot.id);
            setActive(s);
            setSeries((prev) => prev.map((x) => (x.id === s.id ? s : x)));
            setEditingShot(null);
            vibrate('warning');
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Suppression impossible');
        } finally { setBusy(false); }
    };

    const onFixClub = async (clubId: string): Promise<void> => {
        if (!active) return;
        setBusy(true); setError(null);
        try {
            const { series: s } = await changeSeriesClub(active, clubId);
            setActive(s);
            setSeries((prev) => prev.map((x) => (x.id === s.id ? s : x)));
            setFixingClub(false);
            vibrate('tap');
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Changement de club impossible');
        } finally { setBusy(false); }
    };

    const onUndo = async (): Promise<void> => {
        if (!active || active.shots.length === 0) return;
        setBusy(true);
        try {
            const { series: s } = await undoLastShot(active);
            setActive(s);
            setSeries((prev) => prev.map((x) => (x.id === s.id ? s : x)));
            vibrate('warning');
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Annulation impossible');
        } finally { setBusy(false); }
    };

    const onStop = async (status: 'completed' | 'aborted'): Promise<void> => {
        if (!session) return;
        setBusy(true);
        try {
            await finishSession(session.id, status);
            setConfirmStop(false);
            setRunStep(null);
            if (status === 'completed') {
                // Dans une séance, on rend la main au déroulé et non à la
                // synthèse : le joueur veut savoir ce qu'il fait ensuite, pas
                // lire un bilan entre deux seaux. La synthèse reste à un clic
                // depuis la liste, sur l'étape qu'il vient de finir.
                //
                // L'appartenance se demande au serveur plutôt qu'à `runStep` :
                // cet état ne survit pas à un rechargement, et une séance
                // reprise après un écran verrouillé retomberait sur la
                // synthèse en ayant l'air d'avoir quitté le plan.
                //
                // Et si c'était la DERNIÈRE étape, on clôt la séance et on
                // montre son bilan : demander un « Clore » de plus alors qu'il
                // ne reste rien à faire, c'est un clic pour rien, et le joueur
                // veut à ce moment-là savoir ce que la séance a donné.
                const state = await getCurrentRun().catch(() => null);
                const inRun = state?.items.some((i) => i.sessionId === session.id) ?? false;
                if (!inRun) { void navigate(`/entrainement/${session.id}`); return; }
                const reste = state!.items.some((i) => !etapeFaite(i));
                if (!reste && state!.run) {
                    const runId = state!.run.id;
                    // Un échec de clôture ne doit pas bloquer le joueur sur
                    // l'écran de saisie : le déroulé propose encore le bouton.
                    await finishRun(runId, 'completed').catch(() => null);
                    void navigate(`/entrainement/bilan/${runId}`);
                    return;
                }
                void navigate('/entrainement/seance-en-cours');
                return;
            }
            // Abandon : l'exercice a été effacé côté serveur. On revient au
            // choix, sans rien laisser derrière. La séance, elle, survit à
            // l'abandon d'une de ses étapes : on la reprend au même rang, et
            // on rend donc la main au déroulé plutôt qu'au catalogue.
            const wasInRun = runItems.some((i) => i.sessionId === session.id);
            setSession(null); setActive(null); setSeries([]);
            setChosenMode(null); setChosenExercise(null);
            if (wasInRun) { void navigate('/entrainement/seance-en-cours'); return; }
            await load();
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Arrêt impossible');
        } finally { setBusy(false); }
    };

    if (loading) {
        return <div className="flex justify-center py-16"><span className="loading loading-ring loading-lg text-primary" /></div>;
    }

    // ── Écran de lancement ───────────────────────────────────────────────────

    if (!session) {
        const empty = !bag || clubs.length === 0;

        // Écran 1 : que veut-on faire ? Ni date ni liste de clubs ici — on n'a
        // pas encore choisi le mode, ces informations n'ont rien à y faire.
        if (!chosenMode && !chosenExercise) {
            const nextStep = runItems.find((i) => !etapeFaite(i));
            const doneCount = runItems.filter((i) => i.sessionStatus === 'completed').length;

            return (
                /* `text-base-content` explicite : `.fairway-texture` pose une
                   couleur claire pour le fond vert, et les cartes opaques
                   posées dessus en hériteraient — du blanc sur du blanc. Le
                   titre et le lien de retour, eux, sont bien SUR le vert : ils
                   passent au blanc, avec l'ombre portée qui leur garde un bord
                   net si une carte claire remonte dessous au défilement. */
                <div className="space-y-4 py-2 text-base-content">
                    <Link to="/entrainement"
                        className="btn btn-ghost btn-sm gap-2 !text-white drop-shadow-[0_1px_3px_rgba(0,0,0,0.55)]">
                        <ArrowLeft size={16} /> Entraînement
                    </Link>
                    <h1 className="font-display text-2xl font-bold tracking-tight text-white drop-shadow-[0_1px_3px_rgba(0,0,0,0.55)]">
                        Exercices
                    </h1>

                    {/* Le rappel d'une séance ouverte, réduit à un lien.
                        Le déroulé a son propre écran ; le dupliquer ici
                        donnerait deux endroits où lancer la même étape, et deux
                        endroits à tenir d'accord. Ce catalogue sert à autre
                        chose : glisser un exercice EN PLUS du plan, ce qui reste
                        parfaitement légitime au milieu d'une séance. */}
                    {run && (
                        <Link to="/entrainement/seance-en-cours"
                            className="flex items-center gap-3 rounded-box border-2 border-primary bg-primary/10 p-4">
                            <Play size={22} className="shrink-0 text-primary" />
                            <span className="min-w-0 flex-1">
                                <span className="block font-bold">{run.name}</span>
                                <span className="block text-sm opacity-70">
                                    {nextStep
                                        ? `Étape ${doneCount + 1} sur ${runItems.length} — ${nextStep.exerciseName
                                            ?? MODES.find((m) => m.mode === nextStep.mode)?.label ?? 'exercice'}`
                                        : 'Tous les exercices sont faits'}
                                </span>
                            </span>
                            <ChevronRight size={20} className="shrink-0 opacity-50" />
                        </Link>
                    )}

                    {MODES.map((m) => {
                        const stat = modes.find((x) => x.mode === m.mode);
                        return (
                            <button
                                key={m.mode}
                                type="button"
                                onClick={() => setChosenMode(m.mode)}
                                className="relative flex w-full items-start gap-4 rounded-box bg-base-200 p-5 text-left transition-colors hover:bg-base-300"
                            >
                                {m.multiplayer && <MultiplayerBadge />}
                                <ModeIcon mode={m.mode} size={30} className="mt-0.5 shrink-0 text-primary" />
                                <span className="min-w-0 flex-1">
                                    <span className="block text-lg font-bold">{m.label}</span>
                                    <span className="mt-0.5 block text-sm opacity-60">{m.description}</span>
                                    {/* Le repère temporel qui guide : quand
                                        ai-je travaillé ça pour la dernière fois ? */}
                                    <span className="mt-2 block text-xs opacity-50">
                                        {stat?.lastSessionAt
                                            ? `Dernière fois le ${new Date(stat.lastSessionAt).toLocaleDateString('fr-FR', {
                                                weekday: 'long', day: 'numeric', month: 'long',
                                            })} · ${stat.sessionCount} au total`
                                            : 'Jamais pratiqué'}
                                    </span>
                                </span>
                            </button>
                        );
                    })}

                    {/* Le putting est listé ici parce que c'est un exercice, et
                        que le joueur vient chercher « quoi travailler
                        aujourd'hui ». Il MÈNE ailleurs parce qu'il n'en partage
                        rien : ni le sac, ni les séries, ni la solitude. */}
                    <Link to="/putting"
                        className="relative flex w-full items-start gap-4 rounded-box bg-base-200 p-5 text-left transition-colors hover:bg-base-300">
                        <MultiplayerBadge />
                        <Flag size={30} className="mt-0.5 shrink-0 text-primary" />
                        <span className="min-w-0 flex-1">
                            <span className="block text-lg font-bold">Putting</span>
                            <span className="mt-0.5 block text-sm opacity-60">
                                Trou après trou, le nombre de coups pour rentrer.
                                Seul, ou à plusieurs contre ses partenaires.
                            </span>
                        </span>
                    </Link>

                    {/* Les exercices du joueur après les modes intégrés.
                        L'ordre n'est pas un jugement de valeur : les modes sont
                        toujours là, alors qu'une liste personnelle est vide au
                        premier lancement — la mettre en tête montrerait un trou
                        à qui découvre l'application. */}
                    <section>
                        <div className="mb-2 flex items-baseline justify-between gap-2">
                            {/* Ce titre et ce lien sont SUR le vert, pas dans
                                une carte : ils passent au blanc, sans quoi
                                l'encre du thème y tombe à 1,07:1. */}
                            <h2 className="text-sm font-semibold text-white drop-shadow-[0_1px_3px_rgba(0,0,0,0.55)]">
                                Mes exercices
                            </h2>
                            <Link to="/entrainement/exercice/nouveau"
                                className="btn btn-ghost btn-xs gap-1 !text-white drop-shadow-[0_1px_3px_rgba(0,0,0,0.55)]">
                                <Plus size={14} /> Ajouter
                            </Link>
                        </div>

                        {exercises.length === 0 ? (
                            <p className="rounded-box bg-base-200 p-4 text-sm opacity-70">
                                Créez les vôtres : un nom, une consigne, le nombre de balles
                                et de séries, et les clubs autorisés. Ils s'ajoutent ensuite à
                                vos séances.
                            </p>
                        ) : (
                            <ul className="space-y-2">
                                {exercises.map((e) => (
                                    <li key={e.id} className="flex items-stretch gap-2 rounded-box bg-base-200 p-2">
                                        <button type="button"
                                            onClick={() => { setChosenExercise(e); setNotesDraft(e.name); }}
                                            className="min-w-0 flex-1 rounded-field p-2 text-left transition-colors hover:bg-base-300">
                                            <span className="flex items-center gap-1.5">
                                                <span className="min-w-0 truncate font-bold">{e.name}</span>
                                                {/* Le jeton dit d'où vient l'atelier, et donc
                                                    pourquoi il n'a pas de crayon : c'est le
                                                    règlement d'un examen, pas un exercice qu'on
                                                    ajuste. Sans lui, l'absence de crayon
                                                    passerait pour une panne. */}
                                                {e.officiel && (
                                                    <span className="badge badge-sm shrink-0 gap-1 font-bold"
                                                        style={{
                                                            backgroundColor: 'var(--tuile)',
                                                            color: 'var(--tuile-encre)',
                                                            borderColor: 'transparent',
                                                        }}>
                                                        <Award size={11} aria-hidden="true" /> ffgolf
                                                    </span>
                                                )}
                                            </span>
                                            {e.description && (
                                                <span className="block truncate text-sm opacity-60">{e.description}</span>
                                            )}
                                            <span className="mt-1 block text-xs opacity-50">
                                                {describe(e.ballsPerRound, e.rounds)}
                                                {e.lastSessionAt
                                                    ? ` · dernière fois le ${new Date(e.lastSessionAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}`
                                                    : ' · jamais fait'}
                                                {e.successRate != null && ` · ${Number(e.successRate).toFixed(0)} %`}
                                            </span>
                                        </button>
                                        {/* Pas de crayon sur un atelier fédéral : le
                                            serveur refuserait l'écriture, et un formulaire
                                            qu'on remplit avant d'appendre qu'il ne
                                            s'enregistre pas est pire que pas de formulaire. */}
                                        {!e.officiel && (
                                            <Link to={`/entrainement/exercice/${e.id}`}
                                                aria-label={`Modifier ${e.name}`}
                                                className="btn btn-ghost btn-sm btn-square self-center">
                                                <Pencil size={16} />
                                            </Link>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>

                    {error && <div role="alert" className="alert alert-error text-sm"><span>{error}</span></div>}

                    <Link to="/entrainement/historique"
                        className="btn btn-ghost btn-block gap-2 !text-white drop-shadow-[0_1px_3px_rgba(0,0,0,0.55)]">
                        <BarChart3 size={18} /> Mes séances passées
                    </Link>
                </div>
            );
        }

        // Écran 2 : le mode est choisi, on cale la séance.
        return (
            <div className="space-y-5 py-2 text-base-content">
                <button type="button"
                    onClick={() => { setChosenMode(null); setChosenExercise(null); setRunStep(null); }}
                    className="btn btn-ghost btn-sm gap-2 !text-white drop-shadow-[0_1px_3px_rgba(0,0,0,0.55)]">
                    <ArrowLeft size={16} /> Exercices
                </button>
                <h1 className="font-display text-2xl font-bold tracking-tight text-white drop-shadow-[0_1px_3px_rgba(0,0,0,0.55)]">
                    {chosenExercise?.name ?? MODES.find((m) => m.mode === chosenMode)?.label}
                </h1>

                {/* La consigne, en entier, juste avant de démarrer.
                    C'est le dernier moment où on la lit : après, on a un club
                    dans les mains et l'écran est à un mètre. */}
                {chosenExercise && (
                    <div className="rounded-box bg-base-200 p-4">
                        {chosenExercise.description && (
                            <p className="text-sm">{chosenExercise.description}</p>
                        )}
                        <p className={`text-xs opacity-60 ${chosenExercise.description ? 'mt-2' : ''}`}>
                            {describe(chosenExercise.ballsPerRound, chosenExercise.rounds)}
                            {chosenExercise.clubTemplateIds.length > 0
                                && ` · ${chosenExercise.clubTemplateIds.length} type${chosenExercise.clubTemplateIds.length > 1 ? 's' : ''} de club autorisé${chosenExercise.clubTemplateIds.length > 1 ? 's' : ''}`}
                        </p>
                    </div>
                )}

                {runStep && run && (
                    <p className={`text-sm ${SUR_LE_VERT}`}>
                        Étape {runStep.position} sur {runItems.length} de « {run.name} ».
                    </p>
                )}

                {empty ? (
                    <div className="rounded-box border border-base-300 bg-base-100 p-5 shadow-sm">
                        {/* Une carte OPAQUE. Un voile de dix pour cent de laiton
                            sur du vert profond reste du vert profond : l'encre
                            sombre du conteneur y était aussi illisible
                            qu'ailleurs, et c'est précisément l'écran qui
                            explique à un joueur tout neuf pourquoi il ne peut
                            pas encore commencer. */}
                        <p className="font-semibold">
                            {bag ? 'Votre sac est vide' : "Vous n'avez pas encore de sac"}
                        </p>
                        <p className="mt-1 text-sm opacity-80">
                            {bag
                                ? `Composez « ${bag.name} » avec vos clubs — ${MAX_CLUBS_PER_BAG} au maximum — avant de démarrer un exercice.`
                                : 'Créez un sac et placez-y vos clubs pour pouvoir enregistrer des coups.'}
                        </p>
                        <Link to="/materiel" className="btn btn-warning mt-4 gap-2 tap-target">
                            <Briefcase size={18} /> Aller à Mon matériel
                        </Link>
                    </div>
                ) : (
                    <>
                        <label className="block w-full">
                            <span className={`mb-1 block text-sm ${SUR_LE_VERT}`}>Date et heure</span>
                            <input
                                type="datetime-local"
                                className="input input-bordered w-full"
                                value={startedAtDraft || toLocalInput(new Date().toISOString())}
                                onChange={(e) => setStartedAtDraft(e.target.value)}
                            />
                        </label>

                        {/* Le sac en une ligne : de quoi vérifier qu'on part
                            avec le bon, sans réafficher les quatorze clubs —
                            on les verra au moment de choisir.

                            Le mot « Sac » est écrit devant : un sac nommé
                            « défaut » ou « perso » ne dit pas de lui-même ce
                            qu'on est en train de choisir. */}
                        {/* Le choix du sac se fait ICI, pas dans Mon matériel.
                            « Changer » y renvoyait, ce qui obligeait à quitter
                            l'écran, à désigner un sac actif, puis à revenir —
                            alors qu'on veut simplement dire avec quel sac on
                            joue aujourd'hui. Le sac actif reste le défaut. */}
                        <div className="rounded-box bg-base-200 px-4 py-3">
                            <div className="flex items-center justify-between gap-3">
                                <span className="min-w-0 text-sm">
                                    <span className="block text-xs uppercase tracking-wide opacity-50">Sac</span>
                                    <span className="block truncate font-semibold">{bag.name}</span>
                                    <span className="block text-xs opacity-60">
                                        {clubs.length} club{clubs.length > 1 ? 's' : ''}
                                    </span>
                                </span>
                                {usableBags.length > 1 && (
                                    <button type="button" className="btn btn-ghost btn-xs shrink-0"
                                        onClick={() => setPickingBag((v) => !v)}>
                                        {pickingBag ? 'Fermer' : 'Changer'}
                                    </button>
                                )}
                            </div>

                            {pickingBag && (
                                <div className="mt-3 space-y-1 border-t border-base-300 pt-3">
                                    {usableBags.map((b) => (
                                        <button key={b.id} type="button"
                                            onClick={() => { setChosenBagId(b.id); setPickingBag(false); }}
                                            className={`flex w-full items-center justify-between gap-3 rounded-field px-3 py-2 text-left text-sm transition-colors ${
                                                b.id === bag.id ? 'bg-primary text-primary-content' : 'hover:bg-base-300'
                                            }`}>
                                            <span className="min-w-0">
                                                <span className="block truncate font-semibold">{b.name}</span>
                                                {b.description && (
                                                    <span className="block truncate text-xs opacity-70">{b.description}</span>
                                                )}
                                            </span>
                                            <span className="shrink-0 text-xs opacity-70">
                                                {b.clubs.length} club{b.clubs.length > 1 ? 's' : ''}
                                            </span>
                                        </button>
                                    ))}
                                    <Link to="/materiel" className="btn btn-ghost btn-xs mt-1 gap-2">
                                        <Briefcase size={14} /> Composer mes sacs
                                    </Link>
                                </div>
                            )}
                        </div>

                        {/* Le mémo du mode libre.
                            C'est ce qui définit l'exercice : l'application
                            n'impose ici ni rythme, ni nombre de balles, et sans
                            cette phrase deux séances libres à trois semaines
                            d'écart sont indiscernables dans l'historique.
                            Demandé au lancement, tant qu'on sait encore ce qu'on
                            est venu travailler. */}
                        {chosenMode === 'free' && !chosenExercise && (
                            <label className="block w-full">
                                <span className={`mb-1 block text-sm ${SUR_LE_VERT}`}>Votre exercice</span>
                                <input
                                    type="text"
                                    maxLength={200}
                                    className="input input-bordered w-full"
                                    placeholder="Pause au backswing"
                                    value={notesDraft}
                                    onChange={(e) => setNotesDraft(e.target.value)}
                                />
                                <span className={`mt-1 block text-xs opacity-80 ${SUR_LE_VERT}`}>
                                    Apparaîtra dans vos séances passées. Modifiable ensuite.
                                </span>
                            </label>
                        )}

                        <button type="button" onClick={() => void onStart()} disabled={busy}
                            className="btn btn-primary h-20 w-full gap-3 text-lg font-bold">
                            <Play size={26} /> Démarrer
                        </button>
                    </>
                )}

                {error && <div role="alert" className="alert alert-error text-sm"><span>{error}</span></div>}
            </div>
        );
    }

    // ── Séance en cours ──────────────────────────────────────────────────────

    /** L'étape de séance que cet exercice occupe, s'il en occupe une.
     *  Reconstruite depuis le serveur, donc juste après un rechargement. */
    const currentRunItem = runItems.find((i) => i.sessionId === session.id) ?? null;
    /** Ce qui vient après, pour pouvoir le NOMMER au moment de clore.
     *  « Passer à la suite » sans dire à quoi ne vaut guère mieux que
     *  « Arrêter » : dans les deux cas, on ne sait pas ce qu'on déclenche. */
    const nextRunItem = currentRunItem
        ? runItems.find((i) => i.position > currentRunItem.position
            && !etapeFaite(i)) ?? null
        : null;
    const nextRunLabel = nextRunItem
        ? nextRunItem.exerciseName
            ?? MODES.find((m) => m.mode === nextRunItem.mode)?.label
            ?? 'exercice suivant'
        : null;

    const activeClub = clubs.find((c) => c.id === active?.clubId);

    /* Le sac RESTREINT aux types que l'exercice autorise.
     *
     * La règle porte sur des types — « le fer 9 » — et non sur des clubs :
     * c'est ce qui la garde valable après un changement de matériel, et c'est
     * pourquoi le sac doit dire de quel type relève chacun de ses clubs.
     *
     * Une liste vide vaut « club libre », comme dans l'éditeur.
     *
     * Et si AUCUN club du sac ne répond à la règle — on a composé l'exercice
     * pour un club qu'on n'a pas encore, ou on est sorti avec un autre sac —
     * la règle ne peut pas s'appliquer : on rend le sac entier plutôt que de
     * laisser le joueur devant une grille vide, et on le dit. Un écran qui
     * bloque sans expliquer est pire qu'une règle non tenue. */
    const typesPermis = new Set(session.exerciseClubTemplateIds ?? []);
    const clubsPermis = typesPermis.size === 0
        ? clubs
        : clubs.filter((c) => c.templateId != null && typesPermis.has(c.templateId));
    const regleClubsTenue = clubsPermis.length > 0;
    const clubsAuChoix = regleClubsTenue ? clubsPermis : clubs;
    const clubHorsRegle = activeClub != null && typesPermis.size > 0 && regleClubsTenue
        && !clubsPermis.some((c) => c.id === activeClub.id);

    /* Point de départ du sélecteur de distance, par ordre de pertinence.
     *
     *   1. la dernière distance mesurée AVEC CE CLUB dans la séance ;
     *   2. la moyenne réellement atteinte par le joueur avec ce club ;
     *   3. la distance de référence saisie sur le club ;
     *   4. 100 m, faute de mieux.
     *
     * La moyenne passe devant la référence : elle est mesurée, là où la
     * référence est souvent recopiée d'un catalogue. Sur un jeu réel, le même
     * fer affichait 98 m de référence pour 89 m de moyenne — deux appuis sur
     * « − » à chaque coup.
     *
     * Tout est en mètres ; le sélecteur convertit pour l'affichage. */
    const initialDistanceM =
        series
            .filter((s) => s.clubId === active?.clubId)
            .flatMap((s) => s.shots)
            .filter((s) => s.distanceM != null)
            .at(-1)?.distanceM
        ?? activeClub?.avgDistanceM
        ?? activeClub?.referenceDistanceM
        ?? 100;

    // Le mode de la SÉANCE, pas celui qu'on vient de cliquer : une séance
    // reprise après un écran verrouillé n'est pas passée par l'écran de choix.
    const sessionMode = (session.mode as TrainingModeCode) ?? 'triple_shot';
    /* La taille d'une série vient de l'EXERCICE quand il en fixe une.
     *
     * Un exercice personnalisé tourne sur le moteur du mode libre, dont la
     * série n'a pas de taille : « 3 balles par série » s'affichait au lancement
     * puis n'engageait rien, et l'on pouvait en taper douze. La borne composée
     * devient la taille de la série, et l'écran retombe sur la même mécanique
     * que le Triple Shot : autant de pastilles que de balles, et la série se
     * ferme d'elle-même à la dernière. */
    const seriesSize = session.exerciseBalls ?? MODE_SERIES_SIZE[sessionMode] ?? null;
    const suggestedShots = seriesSize != null
        ? null
        : MODE_SUGGESTED_SHOTS[sessionMode] ?? null;

    /* Le NOMBRE DE SÉRIES demandé, qui n'existait nulle part.
     *
     * Le compositeur demande « 3 balles × 5 séries » ; l'écran comptait des
     * séries sans jamais dire combien il en fallait, et rien ne se terminait.
     *
     * On compte les séries FINIES — close, ou pleine — et non les séries
     * ouvertes : celle qu'on est en train de jouer n'est pas encore faite, et
     * l'annoncer comme telle ferait finir l'exercice une série trop tôt. */
    /* L'atelier est-il noté en POINTS ?
     *
     * Un critère de réussite change deux choses à l'écran : le pavé se réduit
     * à deux réponses, et le bandeau compte des points sur un total connu
     * d'avance plutôt qu'un taux. Le reste — séries, durée, correction d'une
     * balle — ne bouge pas : c'est le même moteur, avec une autre question. */
    const critere = session.exerciseSuccessLabel ?? null;
    /** Le barème : une balle réussie vaut un point, le maximum vaut le nombre
     *  de balles. Il n'a pas de colonne à lui, sans quoi les deux pourraient se
     *  contredire et l'une des deux aurait tort. */
    const pointsMax = critere != null && session.exerciseBalls != null
        ? session.exerciseBalls * (session.exerciseRounds ?? 1)
        : null;

    const seriesDemandees = session.exerciseRounds ?? null;
    const seriesFaites = series.filter(
        (s) => s.completedAt != null || (seriesSize != null && s.shots.length >= seriesSize),
    ).length;
    const exerciceFini = seriesDemandees != null && seriesFaites >= seriesDemandees;

    const shotsDone = active?.shots.length ?? 0;
    // Série à taille fixe : elle se ferme d'elle-même. Série libre : elle
    // n'est close que lorsque le joueur l'a dit.
    const seriesComplete = seriesSize !== null
        ? shotsDone >= seriesSize
        : active?.completedAt != null;
    const totalShots = series.reduce((n, s) => n + s.shots.length, 0);
    const totalCenter = series.reduce(
        (n, s) => n + s.shots.filter((x) => x.outcome === 'center').length, 0);

    /* Le club à proposer après trois balles.
     *
     *  Le PRÉCÉDENT dans l'ordre du sac, c'est-à-dire un cran plus long : après
     *  le fer 9, le fer 8. C'est le sens dans lequel on remonte un sac au
     *  practice, et la règle est assez simple pour que le joueur la prévoie —
     *  ce qui compte plus que de l'optimiser.
     *
     *  Arrivé au club le plus long, on repart du plus court : le mode s'appelle
     *  Triple Shot parce qu'on alterne, et une fin de liste qui ne propose plus
     *  rien romprait l'enchaînement au moment où il est le plus utile. */
    const nextClub: BagClub | undefined = (() => {
        // Dans le sac PERMIS : proposer un club que l'exercice interdit serait
        // proposer d'enfreindre sa propre règle.
        if (seriesSize === null || clubsAuChoix.length < 2 || !active) return undefined;
        const at = clubsAuChoix.findIndex((c) => c.id === active.clubId);
        if (at < 0) return undefined;
        return clubsAuChoix[(at - 1 + clubsAuChoix.length) % clubsAuChoix.length];
    })();

    /** Les clubs à travailler, d'après les séances récentes.
     *
     *  Même règle que le tableau de bord — elle vit dans lib/clubFocus.ts pour
     *  que les deux écrans ne puissent pas se contredire. Restreinte au sac du
     *  jour : conseiller un club qu'on n'a pas sous la main serait un conseil
     *  qu'on ne peut pas suivre. */
    const focus = clubsToFocus(stats).filter(
        (c) => clubsAuChoix.some((x) => x.id === c.clubId));
    const focusIds = new Set(focus.map((c) => c.clubId));
    /* Faibles d'un côté, inconnus de l'autre : la marque est la même, la phrase
       qui l'explique ne peut pas l'être. */
    const faiblesIds = new Set(clubsToWork(stats).map((c) => c.clubId));
    const aTravailler = focus.filter((c) => faiblesIds.has(c.clubId)).map((c) => c.label);
    const aDecouvrir = focus.filter((c) => !faiblesIds.has(c.clubId)).map((c) => c.label);

    /* Le récapitulatif par club de la séance a disparu d'ici.

       Il servait à marquer « déjà joué · 2 séries · 6 balles · 50 % » sous
       chaque club de la liste. Les tuiles portent désormais la fiabilité TOUTE
       PÉRIODE, la même qu'au parcours — sans quoi la même carte affichait deux
       chiffres différents pour le même club selon l'écran, et « 0 % » sur un
       club qu'on venait de sortir du sac. Ce que fait la séance en cours se lit
       dans le bandeau vert, à RÉUSSITE. */

    return (
        /* Colonne pleine hauteur, comme l'écran de parcours.
         *
         *  Les deux écrans de jeu ont désormais la même charpente : fond
         *  fairway peint par la coquille, bandeau vert de situation, puis trois
         *  cartes blanches numérotées — où j'en suis, quel club, quel résultat.
         *  Un joueur qui passe du practice au parcours ne réapprend rien.
         *
         *  `text-base-content` est explicite : `.fairway-texture` pose une
         *  couleur de texte claire pour le fond vert, et toute carte opaque
         *  posée dessus en hériterait — du blanc sur du blanc. C'est le bogue
         *  de contraste le plus récurrent du projet.
         *
         *  `dvh` et non `vh` : sur mobile la barre d'URL se rétracte et `vh`
         *  reste figé sur la hauteur dépliée, ce qui pousse la dernière rangée
         *  sous l'écran. Le retrait de 8,5 rem couvre l'en-tête de
         *  l'application et les marges de la coquille ; il est vérifié à la
         *  mesure, pas estimé. */
        <div className="flex min-h-[calc(100dvh-8.5rem)] flex-col gap-2 py-2 text-base-content">
            {/* L'en-tête vit SUR le fairway : texte blanc, et le bouton de
                sortie en carte blanche cerclée de rouge — seule façon de rester
                lisible sur le vert sans crier. */}
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    {/* Le nom de l'exercice prime sur celui du mode : un
                        exercice personnalisé tourne en mode libre, et
                        « Exercice libre » les appellerait tous pareil au moment
                        précis où il faut savoir lequel on fait. */}
                    <h1 className="font-display truncate text-xl font-bold tracking-tight text-white">
                        {session.exerciseName
                            ?? MODES.find((m) => m.mode === sessionMode)?.label
                            ?? 'Exercice en cours'}
                    </h1>
                    {/* Le rang dans la séance, quand il y en a une : c'est le
                        repère qu'on cherche en rallumant l'écran. */}
                    {currentRunItem && run && (
                        <p className="truncate text-xs font-bold text-white/85">
                            {run.name}
                            <span className="text-white/40">{' · '}</span>
                            étape {currentRunItem.position} sur {runItems.length}
                        </p>
                    )}
                </div>
            </div>

            {/* Le bandeau de situation, en scène : le même panneau vert profond
                que le tour en cours, aux mêmes quatre valeurs près. Il dit où
                l'on en est de la SÉANCE, et cela vaut pour les trois étapes
                d'en dessous — c'est l'entête du carnet, pas sa première ligne.

                Le taux de réussite est en laiton : c'est LA valeur qu'on
                vérifie ici, comme le numéro du trou sur le parcours.

                Une arête claire redécoupe le panneau : à 32 % de clarté contre
                30 % pour le haut du dégradé, il s'y dissoudrait. */}
            <div className="grid grid-cols-4 gap-1 rounded-field border border-white/15 bg-[var(--fairway-deep)] px-2 py-2 text-center shadow-md">
                <Valeur mot="durée"
                    v={formatDurationShort(Math.max(0, now - new Date(session.startedAt).getTime()))} />
                <Valeur mot="balles" v={String(totalShots)} />
                {/* Le compte sur le CONTRAT dès que l'exercice en fixe un :
                    « 2/3 » dit où l'on en est, là où un nombre de séries
                    ouvertes ne se comparait à rien. Le mot ne change pas pour
                    autant — c'est le même objet que l'étape ① juste dessous. */}
                {seriesDemandees != null
                    ? <Valeur mot="séries" v={`${seriesFaites}/${seriesDemandees}`} />
                    : <Valeur mot="séries" v={String(series.length)} />}
                {/* Des POINTS sur un total connu d'avance, quand l'atelier
                    en a un. « 60 % » ne dit pas si l'on est reçu ; « 3/5 », si.
                    C'est le chiffre que le joueur vient chercher entre deux
                    balles, et celui qu'on annonce à l'examinateur. */}
                {pointsMax != null
                    ? <Valeur mot="points" accent v={`${totalCenter}/${pointsMax}`} />
                    : (
                        <Valeur mot="réussite" accent
                            v={totalShots > 0 ? `${Math.round((totalCenter * 100) / totalShots)}%` : '—'} />
                    )}
            </div>

            {/* L'exercice, sous les yeux pendant qu'on le fait. C'est tout
                l'objet du mode libre : la consigne qu'on s'est donnée est ce
                qu'on oublie en premier après trois balles.

                La consigne détaillée d'un exercice personnalisé passe devant le
                mémo, qui n'est alors que son nom — déjà en titre. */}
            {/* Le RÈGLEMENT prime sur le mémo quand l'atelier en a un : c'est
                la mise en place — à quelle distance, sur quelle cible — et on
                la relit avant chaque série, pas seulement au lancement. */}
            {(session.exerciseCriteria ?? session.exerciseDescription ?? session.notes) && (
                <p className="rounded-box border border-base-300 bg-base-100 px-3 py-2 text-sm italic shadow-sm">
                    « {session.exerciseCriteria ?? session.exerciseDescription ?? session.notes} »
                </p>
            )}

            {error && (
                <div role="alert" className="alert alert-error text-sm">
                    <span>{error}</span>
                    <button type="button" className="btn btn-ghost btn-xs" onClick={() => setError(null)}>OK</button>
                </div>
            )}

            {/* ① LES BALLES — où l'on en est de la série en cours.

                La carte s'intitulait « Série », et elle ne compte pas des
                séries : elle compte les BALLES de celle qu'on joue. Les séries,
                elles, se comptent dans le bandeau vert, à SÉRIES. Une carte doit
                porter le nom de ce qu'elle montre, sans quoi deux cartes
                voisines semblent dire la même chose de deux façons — et l'on
                croit les deux réglages inversés.

                Les pastilles sont montrées DÈS LE DÉPART, comme les cases de
                coups du parcours : rien n'annonçait sinon où l'information
                allait s'inscrire, et l'écran changeait de forme sous le pouce
                à la première balle. */}
            <Etape n={1} titre="Balles"
                /* Annulable jusqu'au bout, série close comprise.
                   Le bouton disparaissait à la troisième balle : c'est
                   exactement celle qu'on se trompe le plus souvent à saisir,
                   puisqu'on tape en enchaînant. Annuler rouvre la série — le
                   serveur retire l'horodatage de clôture — et l'enchaînement se
                   représente ensuite. */
                action={shotsDone > 0 ? (
                    <button type="button" onClick={() => void onUndo()} disabled={busy}
                        aria-label="Annuler la dernière balle"
                        className="relative flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5
                                   text-[11px] font-bold uppercase tracking-wide text-error
                                   transition-colors hover:bg-base-200 before:absolute before:-inset-2 before:content-['']">
                        <Undo2 size={13} aria-hidden="true" /> Annuler
                    </button>
                ) : undefined}>
                {seriesSize !== null ? (
                    /* Taille fixe : autant de pastilles que de balles. Où en
                       est la série, visible sans compter. */
                    <div className="flex gap-2">
                        {Array.from({ length: seriesSize }, (_, i) => {
                            const shot = active?.shots[i];
                            // Chaque balle saisie est un bouton : la toucher
                            // ouvre sa correction. C'est le geste qu'on cherche
                            // quand on voit une erreur, et il n'y a rien
                            // d'autre à apprendre.
                            return shot ? (
                                <button key={shot.id} type="button"
                                    onClick={() => setEditingShot(shot)}
                                    aria-label={`Corriger la balle ${i + 1}`}
                                    className={`flex h-12 flex-1 flex-col items-center justify-center gap-0.5
                                                rounded-field transition-opacity hover:opacity-80 ${
                                        shot.outcome === 'center' ? 'bg-primary text-primary-content' : ''}`}
                                    style={shot.outcome === 'center' ? undefined : {
                                        background: 'var(--brique)', color: 'var(--brique-encre)',
                                    }}>
                                    {/* La marque du bouton, pas son mot : check
                                        laiton pour le bon contact, croix pour le
                                        mauvais, exactement comme la rangée de
                                        coups du parcours. Le joueur retrouve ce
                                        qu'il vient de toucher. */}
                                    {shot.outcome === 'center'
                                        ? <Check size={20} strokeWidth={3.5} aria-hidden="true" className="text-accent" />
                                        : <X size={20} strokeWidth={3.5} aria-hidden="true" />}
                                    {shot.distanceM != null && (
                                        <span className="text-[10px] font-bold leading-none tabular-nums">
                                            {formatDistance(shot.distanceM, unit)}
                                        </span>
                                    )}
                                </button>
                            ) : pendingDistance && i === (active?.shots.length ?? 0) ? (
                                /* La balle est FRAPPÉE mais pas encore écrite :
                                   le serveur ne la reçoit qu'avec sa distance.
                                   Sa case restait un numéro en pointillé gris,
                                   identique aux deux suivantes — on venait de
                                   taper, et rien à l'écran ne l'avait retenu.
                                   Le laiton dit « en cours » : quelque chose est
                                   commencé ici, il manque une réponse. */
                                <span key={i} aria-hidden="true"
                                    className="flex h-12 flex-1 flex-col items-center justify-center gap-0.5
                                               rounded-field border-2 border-dashed"
                                    style={{
                                        borderColor: 'var(--color-accent)',
                                        color: 'var(--brass-ink)',
                                        backgroundColor: 'color-mix(in oklab, var(--color-accent) 18%, transparent)',
                                    }}>
                                    <Check size={18} strokeWidth={3.5} />
                                    <span className="text-[9px] font-bold uppercase leading-none">distance ?</span>
                                </span>
                            ) : (
                                <span key={i} aria-hidden="true"
                                    className="flex h-12 flex-1 items-center justify-center rounded-field
                                               border border-dashed border-base-300 text-xs font-bold
                                               tabular-nums text-base-content/45">
                                    {i + 1}
                                </span>
                            );
                        })}
                    </div>
                ) : (
                    /* Série libre : un compteur et le fil des balles. Le repère
                       (10) est une suggestion, pas une limite — la barre
                       continue au-delà. */
                    <div>
                        <div className="flex items-baseline justify-between">
                            <span className="font-score text-2xl leading-none">
                                {shotsDone}
                                {suggestedShots && (
                                    <span className="text-base font-normal opacity-50">
                                        {' / '}{suggestedShots}
                                    </span>
                                )}
                            </span>
                            <span className="text-xs opacity-60">
                                balle{shotsDone > 1 ? 's' : ''} dans cette série
                            </span>
                        </div>

                        {suggestedShots && (
                            <progress
                                className="progress progress-primary mt-1 h-1.5 w-full"
                                value={Math.min(shotsDone, suggestedShots)}
                                max={suggestedShots}
                            />
                        )}

                        {shotsDone > 0 && active && (
                            <div className="mt-2 flex gap-1.5 overflow-x-auto pb-0.5
                                            [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                                {active.shots.map((shot, i) => (
                                    <button key={shot.id} type="button"
                                        onClick={() => setEditingShot(shot)}
                                        aria-label={`Corriger la balle ${i + 1}`}
                                        className={`flex h-7 shrink-0 items-center gap-1.5 rounded-field px-2
                                                    transition-opacity hover:opacity-80 ${
                                            shot.outcome === 'center' ? 'bg-primary text-primary-content' : ''}`}
                                        style={shot.outcome === 'center' ? undefined : {
                                            background: 'var(--brique)', color: 'var(--brique-encre)',
                                        }}>
                                        <span className="text-[10px] font-bold leading-none tabular-nums">
                                            {i + 1}
                                        </span>
                                        <span aria-hidden="true" className="h-3.5 w-px bg-current opacity-25" />
                                        {shot.outcome === 'center'
                                            ? <Check size={15} strokeWidth={3.5} aria-hidden="true" className="text-accent" />
                                            : <X size={15} strokeWidth={3.5} aria-hidden="true" />}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </Etape>

            {/* ② CHOIX DU CLUB — le même module que sur le parcours.

                Pas « la même chose » : le même composant. Icône, polices,
                tuiles, chevron, bouton « Garder » — tout vient de là-bas, et
                rien ne peut dériver d'un écran à l'autre.

                Ce que le practice a de particulier tient en trois paramètres :
                la puce « à travailler » en haut à droite, la même marque sur les
                tuiles concernées, et le lien de correction sous la grille. Sur
                le parcours ces trois-là restent vides.

                Tant qu'aucune série n'est ouverte le sac est déplié d'office :
                c'est le premier geste de la séance, il n'a pas à se chercher
                derrière un chevron. */}
            <ChoixDuClub
                n={2}
                disabled={busy}
                ouvert={!changementDeClub && (sacOuvert || active == null)}
                onOuvrir={setSacOuvert}
                courantId={active?.clubId ?? null}
                onChoisir={(id) => void passerAuClub(id)}
                clubs={clubsAuChoix.map((c) => ({
                    id: c.id,
                    label: c.label,
                    category: c.category,
                    distance: c.referenceDistanceM != null
                        ? formatDistance(c.referenceDistanceM, unit) : null,
                    /* La fiabilité TOUTE PÉRIODE, celle que porte aussi la
                       carte du parcours. Le taux de la séance en cours donnait
                       « 0 % » sur un club qu'on vient de sortir du sac, et deux
                       chiffres différents pour le même club selon l'écran. Ce
                       que fait la séance se lit dans le bandeau vert, à
                       RÉUSSITE. */
                    taux: c.shots > 0 ? Math.round((c.center * 100) / c.shots) : null,
                    marque: focusIds.has(c.id)
                        ? <MarqueTuile><Wrench size={12} aria-hidden="true" /></MarqueTuile>
                        : undefined,
                }))}
                /* La distance annoncée, puis la fiabilité — ici celle de la
                   SÉANCE en cours, puisque c'est elle qu'on est venu faire
                   bouger. Même paire de chiffres, même ordre que sur le
                   parcours. */
                sousTitre={activeClub
                    ? ([
                        activeClub.referenceDistanceM != null
                            ? formatDistance(activeClub.referenceDistanceM, unit) : null,
                        activeClub.shots > 0
                            ? `fiabilité ${Math.round((activeClub.center * 100) / activeClub.shots)} %`
                            : null,
                    ].filter(Boolean).join(' · ') || 'jamais travaillé')
                    : 'choisissez dans le sac'}
                legende={[
                    aTravailler.length > 0 ? `à travailler : ${aTravailler.join(', ')}` : null,
                    aDecouvrir.length > 0 ? `à découvrir : ${aDecouvrir.join(', ')}` : null,
                ].filter(Boolean).join(' · ') || undefined}
                /* La place du jeton « suggéré » du parcours. Ici l'avis ne vient
                   pas d'une distance à couvrir mais des dernières séances : ce
                   club est sous votre moyenne, il mérite des balles. Même place,
                   même rôle — un avis, pas une consigne. */
                /* La RÈGLE, écrite là où elle s'applique.
                   Restreindre la grille sans le dire donnerait un sac qui a
                   perdu des clubs sans raison visible — on chercherait le fer 7
                   en croyant l'avoir oublié à la maison. */
                note={typesPermis.size > 0 ? (
                    <p className="mt-1.5 flex items-start gap-1.5 rounded-field px-2 py-1.5 text-xs font-semibold"
                        style={!regleClubsTenue || clubHorsRegle
                            ? { backgroundColor: 'var(--brique)', color: 'var(--brique-encre)' }
                            : { backgroundColor: 'var(--tuile)', color: 'var(--tuile-encre)' }}>
                        <Wrench size={12} aria-hidden="true" className="mt-0.5 shrink-0" />
                        <span>
                            {!regleClubsTenue
                                ? "Aucun club de ce sac ne correspond aux types demandés par l’exercice : tout le sac reste ouvert."
                                : clubHorsRegle
                                    ? `Ce club sort de l’exercice, qui demande ${clubsPermis.map((c) => c.label).join(', ')}.`
                                    : `Cet exercice se joue avec ${clubsPermis.map((c) => c.label).join(', ')}.`}
                        </span>
                    </p>
                ) : undefined}
                action={activeClub && focusIds.has(activeClub.id) ? (
                    <span className="flex shrink-0 items-center gap-1 rounded-full bg-warning px-2.5 py-0.5 text-[11px] font-bold text-warning-content">
                        <Wrench size={11} aria-hidden="true" /> à travailler
                    </span>
                ) : undefined}
                /* Le seul geste du practice qui n'existe pas sur le parcours :
                   « je me suis trompé de club » garde les balles et ne clôt pas la
                   série, là où toucher une tuile passe au club suivant. Les deux
                   ne font pas la même chose aux balles déjà saisies, et il n'y a
                   pas de façon honnête de dire la différence dans une tuile —
                   d'où ce lien discret, qui ouvre la feuille où elle est
                   expliquée. */
                /* La raison du choix, que le practice n'avait pas : la carte y
                   montrait un club sans jamais dire ce qui le désigne. Le
                   parcours donne la distance qui tombe le plus près ; ici
                   l'argument est la MOYENNE — ce club décroche sur les
                   dernières séances, il mérite des balles. Même panneau, même
                   place, même encre laiton.

                   Rien à dire quand le club en cours n'est pas dans la liste :
                   on ne fabrique pas une justification pour un choix qui n'en
                   demande pas. */
                sousLaGrille={(
                    <>
                        {activeClub && (
                            <PourquoiCeClub label={activeClub.label}>
                                {/* Le panneau est TOUJOURS là dès qu'un club est
                                    en main, comme sur le parcours. Ce qui change
                                    est ce qu'il a à dire : un club qui décroche
                                    mérite qu'on le nomme, les autres reçoivent
                                    leur bilan. Un panneau qui apparaît et
                                    disparaît selon le club ferait croire à une
                                    alerte là où il n'y a qu'un rappel. */}
                                {focusIds.has(activeClub.id) ? (
                                    <RaisonLigne>
                                        {faiblesIds.has(activeClub.id)
                                            ? `Vos dernières séances le placent sous votre moyenne : c'est celui qui a le plus à gagner à quelques balles.`
                                            : `Vous l'avez trop peu sorti pour qu'on puisse en dire quoi que ce soit : quelques balles et il aura un chiffre.`}
                                    </RaisonLigne>
                                ) : (
                                    <RaisonLigne>
                                        {activeClub.referenceDistanceM != null ? (
                                            <>
                                                Vous l'annoncez à{' '}
                                                <span className="tabular-nums">
                                                    {formatDistance(activeClub.referenceDistanceM, unit)}
                                                </span>. Chaque série de balles précise ce chiffre.
                                            </>
                                        ) : (
                                            <>Aucune distance annoncée sur ce club : les balles la diront.</>
                                        )}
                                    </RaisonLigne>
                                )}
                                <RaisonLigne>
                                    {activeClub.shots > 0 ? (
                                        <>
                                            Sur <span className="tabular-nums">{activeClub.shots}</span> balles
                                            frappées, vous le réussissez{' '}
                                            <span className="tabular-nums">
                                                {Math.round((activeClub.center * 100) / activeClub.shots)} %
                                            </span> du temps.
                                        </>
                                    ) : (
                                        <>Vous ne l'avez encore jamais travaillé : ces balles seront les premières.</>
                                    )}
                                </RaisonLigne>
                            </PourquoiCeClub>
                        )}
                        {/* Le seul geste du practice qui n'existe pas sur le
                            parcours : « je me suis trompé de club » garde les
                            balles et ne clôt pas la série, là où toucher une
                            tuile passe au club suivant. Les deux ne font pas la
                            même chose aux balles déjà saisies, et il n'y a pas
                            de façon honnête de dire la différence dans une
                            tuile. */}
                        {active && shotsDone > 0 && (
                            <button type="button" onClick={() => { setSacOuvert(false); setFixingClub(true); }}
                                className="btn btn-ghost btn-sm mt-2 w-full gap-2 font-semibold"
                                style={{ color: 'var(--brass-ink)' }}>
                                <Repeat size={14} /> Je me suis trompé de club
                            </button>
                        )}
                    </>
                )}
            />

            {/* La sortie, tout en bas — même place que sur le parcours.

                Elle occupait le coin haut droit, où l'on ne va qu'une fois par
                séance. Le mot change avec ce qu'il fait : au milieu d'un
                enchaînement, finir l'exercice fait AVANCER la séance et ne la
                quitte pas. */}
            {/* Placé après l'étape 3 dans le flux : voir plus bas. */}

            {/* ③ LE RÉSULTAT — ce que la balle a fait.

                La dernière étape, et la seule qu'on touche à chaque balle : elle
                prend toute la hauteur qui reste. Le pavé y est en version
                AMPLE — mêmes boutons, mêmes couleurs, même géographie que sur le
                parcours, mais les tuiles prennent la hauteur libre. Ici les
                huit réponses sont l'objet même de la séance : on tape cinquante
                balles pour voir laquelle revient. */}
            {/* L'étape s'étire tant qu'il y a des balles à saisir : le pavé doit
                descendre jusqu'en bas, sous le pouce. Une fois la série close
                elle ne porte plus que deux boutons, et l'étirer laissait quatre
                cents pixels de blanc sous eux. */}
            <Etape n={3} titre="Résultat"
                className={seriesComplete ? 'flex flex-col' : 'flex min-h-0 flex-1 flex-col'}>
                {pendingDistance ? (
                    <DistanceStepper
                        initial={initialDistanceM}
                        onValidate={(d) => { setPendingDistance(false); void applyShot('center', d); }}
                        onSkip={() => { setPendingDistance(false); void applyShot('center', null); }}
                    />
                ) : seriesComplete && exerciceFini ? (
                    /* Le CONTRAT est rempli : toutes les séries demandées sont
                       faites. Continuer à proposer « club suivant » comme action
                       principale reviendrait à ignorer ce que le joueur a
                       composé — c'est précisément ce que faisait l'écran.
                       Terminer devient donc le geste évident.
                       « Une série de plus » reste dessous : la règle s'applique,
                       elle n'enferme pas. */
                    <div className="flex flex-col gap-2">
                        <button type="button" disabled={busy}
                            onClick={() => void onStop('completed')}
                            className="btn btn-primary min-h-[4.5rem] w-full shrink-0 gap-3 shadow-md">
                            <Check size={26} strokeWidth={3} aria-hidden="true"
                                className="shrink-0 text-accent" />
                            <span className="min-w-0 text-left">
                                <span className="font-score block truncate text-2xl leading-[1.15]">
                                    Terminer l’exercice
                                </span>
                                <span className="block text-sm font-normal opacity-80">
                                    {seriesDemandees} série{seriesDemandees !== null && seriesDemandees > 1 ? 's' : ''}
                                    {' sur '}{seriesDemandees}, c’est fait
                                </span>
                            </span>
                        </button>
                        <button type="button" disabled={busy}
                            onClick={() => (nextClub ? void onPickClub(nextClub.id) : setActive(null))}
                            className="btn btn-ghost btn-block shrink-0 gap-2">
                            <RotateCcw size={18} /> Une série de plus
                        </button>
                    </div>
                ) : seriesComplete ? (
                    /* Le bouton NOMME le club suivant, il ne se contente pas
                       d'ouvrir la liste. Trois balles puis on remonte d'un
                       club : après le fer 9, le fer 8. C'est la progression
                       naturelle du Triple Shot, et la nommer évite un écran de
                       choix pour ce qui est le cas normal. Le choix libre reste
                       dessous, à un tap. */
                    <div className="flex min-h-0 flex-1 flex-col gap-2">
                        {/* Plus de `flex-1` : le bouton était étiré sur toute la
                            hauteur restante de l'étape, soit deux cent cinquante
                            pixels pour deux lignes de texte flottant au milieu
                            du vide. Il tient maintenant à sa hauteur, et le
                            reste de la carte respire au lieu d'être poussé. */}
                        <button type="button" disabled={busy}
                            onClick={() => (nextClub ? void onPickClub(nextClub.id) : setActive(null))}
                            className="btn btn-primary min-h-[4.5rem] w-full shrink-0 gap-3 shadow-md">
                            <Target size={26} aria-hidden="true" className="shrink-0 text-accent" />
                            <span className="min-w-0 text-left">
                                <span className="font-score block truncate text-2xl leading-[1.15]">
                                    {nextClub ? nextClub.label : 'Changer de club'}
                                </span>
                                <span className="block text-sm font-normal opacity-80">
                                    {/* Le rang de la série quand l'exercice en
                                        demande un nombre : c'est le repère
                                        qu'on cherche en reposant le club. */}
                                    {seriesDemandees != null
                                        ? `Série ${Math.min(seriesFaites + 1, seriesDemandees)} sur ${seriesDemandees}`
                                        : nextClub
                                            ? `${seriesSize ?? 3} balles suivantes`
                                            : 'la série est terminée'}
                                </span>
                            </span>
                        </button>
                        {nextClub && (
                            <button type="button" disabled={busy}
                                onClick={() => setActive(null)}
                                className="btn btn-outline btn-block h-12 shrink-0 gap-2">
                                <Target size={18} /> Choisir un autre club
                            </button>
                        )}
                        <button type="button" disabled={busy}
                            onClick={() => activeClub && void onPickClub(activeClub.id)}
                            className="btn btn-ghost btn-block shrink-0 gap-2">
                            <RotateCcw size={18} />
                            Refaire {seriesSize ?? 3} balles{activeClub ? ` · ${activeClub.label}` : ''}
                        </button>
                    </div>
                ) : (
                    /* Sans club actif le pavé reste VISIBLE mais inerte : il
                       montre où la séquence mène, exactement comme sur le
                       parcours avant qu'un club soit choisi. */
                    critere != null
                        ? (
                            <PavePoints critere={critere} onPick={onOutcome}
                                disabled={busy || active == null} />
                        )
                        : <PaveCoup onPick={onOutcome} disabled={busy || active == null} />
                )}
            </Etape>

            <button type="button" onClick={() => setConfirmStop(true)}
                className="btn btn-ghost btn-sm mx-auto mt-1 gap-2 font-semibold !text-white/80
                           drop-shadow-[0_1px_3px_rgba(0,0,0,0.55)]">
                {/* Deux gestes, deux icônes. `Square` les portait tous les
                    deux : à quinze pixels à côté d'un texte, un carré vide se
                    lit comme une case à cocher, pas comme un arrêt. Clore une
                    étape de séance est une validation ; quitter est une sortie,
                    et c'est le même mot et la même icône que sur le parcours. */}
                {currentRunItem
                    ? <Check size={15} aria-hidden="true" />
                    : <LogOut size={15} aria-hidden="true" />}
                {currentRunItem ? "Finir l'exercice" : 'Quitter la séance'}
            </button>

            {editingShot && (
                <ShotEditor
                    shot={editingShot}
                    position={(active?.shots.findIndex((s) => s.id === editingShot.id) ?? 0) + 1}
                    busy={busy}
                    onSave={(patch) => void onEditShot(patch)}
                    onDelete={() => void onDeleteShot()}
                    onClose={() => setEditingShot(null)}
                />
            )}

            {/* ── Rattrapage de club, balles conservées ─────────────────────── */}
            {fixingClub && active && (
                <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 sm:items-center">
                    <div className="max-h-[92dvh] w-full max-w-lg overflow-y-auto rounded-t-box bg-base-200 sm:rounded-box">
                        <div className="sticky top-0 flex items-center justify-between bg-base-200 px-4 py-3">
                            <h2 className="text-lg font-bold">Club</h2>
                            <button type="button" className="btn btn-ghost btn-square tap-target"
                                onClick={() => setFixingClub(false)} aria-label="Fermer">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="space-y-2 px-4 pb-4">
                            {/* Deux intentions derrière le même geste, et elles
                                ne font pas la même chose aux balles déjà
                                saisies. Tant que la série est vide, la question
                                ne se pose pas : les deux reviennent au même, on
                                ne la pose donc pas. */}
                            {shotsDone > 0 && (
                                <>
                                    <p className="text-sm font-semibold">
                                        {shotsDone} balle{shotsDone > 1 ? 's' : ''} déjà saisie{shotsDone > 1 ? 's' : ''}
                                        {' '}dans cette série.
                                    </p>
                                    <button type="button" disabled={busy}
                                        onClick={() => { setFixingClub(false); void onChangeClub(); }}
                                        className="btn btn-primary btn-block h-14 justify-start gap-2 tap-target">
                                        <Target size={18} />
                                        <span className="text-left">
                                            <span className="block font-bold">Passer au club suivant</span>
                                            <span className="block text-xs font-normal opacity-80">
                                                Clôt cette série et en ouvre une nouvelle
                                            </span>
                                        </span>
                                    </button>
                                    <p className="pt-2 text-xs font-semibold uppercase tracking-wide opacity-60">
                                        Ou corriger : je me suis trompé de club
                                    </p>
                                    <p className="text-xs opacity-70">
                                        Les balles restent, seul le club changé.
                                    </p>
                                </>
                            )}
                            {clubs.map((c) => (
                                <button key={c.id} type="button" disabled={busy || c.id === active.clubId}
                                    onClick={() => void onFixClub(c.id)}
                                    className={`btn btn-block h-14 justify-start tap-target ${
                                        c.id === active.clubId ? 'btn-primary' : 'btn-neutral'
                                    }`}>
                                    <ClubLabel club={c} />
                                    {c.id === active.clubId && <span className="ml-1"> · actuel</span>}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* ── Confirmation d'arrêt ─────────────────────────────────────── */}
            {confirmStop && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
                    <div className="w-full max-w-sm rounded-box bg-base-200 p-5">
                        {/* Dans une séance, ce dialogue est un PASSAGE, pas une
                            sortie : c'est en clôturant l'exercice qu'on avance
                            d'une étape. Il le dit, et il nomme la suite.

                            Et il ne dit plus « séance » pour parler de
                            l'exercice : le mot désigne maintenant le plan
                            entier, donc l'employer ici laissait croire qu'on
                            allait tout arrêter. */}
                        <h2 className="text-lg font-bold">
                            {currentRunItem
                                ? `Finir « ${session.exerciseName ?? MODES.find((m) => m.mode === sessionMode)?.label ?? 'cet exercice'} » ?`
                                : 'Terminer cet exercice ?'}
                        </h2>
                        <p className="mt-2 text-sm opacity-70">
                            {totalShots} balle{totalShots > 1 ? 's' : ''} enregistrée{totalShots > 1 ? 's' : ''}.
                            {currentRunItem && run && (nextRunLabel
                                ? ` Vous passerez à l'étape ${nextRunItem?.position} de « ${run.name} » : ${nextRunLabel}.`
                                : ` C'est la dernière étape de « ${run.name} ».`)}
                        </p>
                        <div className="mt-4 space-y-2">
                            <button type="button" disabled={busy}
                                onClick={() => void onStop('completed')}
                                className="btn btn-primary btn-block gap-2 tap-target">
                                <Check size={18} />
                                {currentRunItem
                                    ? (nextRunLabel ? 'Finir et passer au suivant' : 'Finir la dernière étape')
                                    : 'Terminer et voir la synthèse'}
                            </button>
                            {/* Abandonner efface : le dire sur le bouton, pas
                                seulement dans une note qu'on ne lit pas.
                                Dans une séance, préciser que seul CET exercice
                                disparaît — le plan, lui, continue. */}
                            <button type="button" disabled={busy}
                                onClick={() => void onStop('aborted')}
                                className="btn btn-outline btn-error btn-block gap-2">
                                <Trash2 size={16} />
                                {currentRunItem ? 'Effacer cet exercice' : 'Abandonner et tout effacer'}
                            </button>
                            <button type="button" onClick={() => setConfirmStop(false)}
                                className="btn btn-ghost btn-block">
                                Continuer cet exercice
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
